import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, User, UsersRound, AlertCircle } from 'lucide-react'
import type { LeagueFormat, MatchFormat } from './types'

interface CreateLeagueDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Form state
  name: string
  onNameChange: (value: string) => void
  format: LeagueFormat
  onFormatChange: (value: LeagueFormat) => void
  matchFormat: MatchFormat
  onMatchFormatChange: (value: MatchFormat) => void
  description: string
  onDescriptionChange: (value: string) => void
  // Actions
  onSubmit: () => void
  saving: boolean
  error: string | null
}

export function CreateLeagueDialog({
  open,
  onOpenChange,
  name,
  onNameChange,
  format,
  onFormatChange,
  matchFormat,
  onMatchFormatChange,
  description,
  onDescriptionChange,
  onSubmit,
  saving,
  error,
}: CreateLeagueDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New League</DialogTitle>
          <DialogDescription>
            Add a new leagues to track rankings and matches.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="league-name">League Name *</Label>
            <Input
              id="league-name"
              placeholder="e.g., Men's Singles A"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="league-format">Format</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => onFormatChange('singles')}
                className={`flex items-center gap-2 justify-center rounded-md border-2 p-3 transition-colors ${
                  format === 'singles'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <User className="h-5 w-5" />
                <span className="font-medium">Singles</span>
              </button>
              <button
                type="button"
                onClick={() => onFormatChange('doubles')}
                disabled
                className={`flex flex-col items-center justify-center rounded-md border-2 p-3 transition-colors relative ${
                  format === 'doubles'
                    ? 'border-primary bg-primary/5'
                    : 'border-border opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center gap-2">
                  <UsersRound className="h-5 w-5" />
                  <span className="font-medium">Doubles</span>
                </div>
                <span className="text-[10px] text-muted-foreground mt-1">Coming soon</span>
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Match Format</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => onMatchFormatChange('bestOf1')}
                className={`flex flex-col items-center justify-center rounded-md border-2 p-3 transition-colors ${
                  matchFormat === 'bestOf1'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <span className="text-lg font-bold">1</span>
                <span className="text-xs text-muted-foreground">Single Game</span>
              </button>
              <button
                type="button"
                onClick={() => onMatchFormatChange('bestOf3')}
                className={`flex flex-col items-center justify-center rounded-md border-2 p-3 transition-colors ${
                  matchFormat === 'bestOf3'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <span className="text-lg font-bold">3</span>
                <span className="text-xs text-muted-foreground">Best of 3</span>
              </button>
              <button
                type="button"
                onClick={() => onMatchFormatChange('bestOf5')}
                className={`flex flex-col items-center justify-center rounded-md border-2 p-3 transition-colors ${
                  matchFormat === 'bestOf5'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <span className="text-lg font-bold">5</span>
                <span className="text-xs text-muted-foreground">Best of 5</span>
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {matchFormat === 'bestOf1' 
                ? 'Each match consists of a single game'
                : `First to win ${matchFormat === 'bestOf3' ? '2' : '3'} games wins the match`
              }
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="league-description">Description (optional)</Label>
            <Textarea
              id="league-description"
              placeholder="Brief description of this league..."
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              maxLength={500}
              rows={3}
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving || !name.trim()}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create League'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
